import { getHours, getMinutes, isBefore, parseISO } from 'date-fns';
import { CancellationDate } from '../types';

export interface CancellationDateFormValues {
	// Required - 'Now'/'Later'
	cancellationDateDescription: CancellationDate['description'];
	// Optional - tied to a 'Later' value
	cancellationDate?: string;
	cancellationTime?: { hours: string; minutes: string };
}

/**
 * Helper function for `getInitialCancellationDateFormValues`, which populates _all_ form fields in the event we're loading data for a 'Later' cancellation date.
 * @param storeDate - The stored cancellation date.
 * @param storeDescription - The stored cancellation description.
 * @returns The form values for the 'Later' cancellation date `{cancellationDateDescription, cancellationDate, cancellationTime}`
 */
export const getLaterDateFormValues = (
	storeDate: CancellationDate['date'],
	storeDescription: CancellationDate['description'] = 'LATER',
): CancellationDateFormValues => {
	const defaultValues: CancellationDateFormValues = { cancellationDateDescription: '' }; // reset the form in event of faulty data
	const nowValues: CancellationDateFormValues = { cancellationDateDescription: 'NOW' }; // 'NOW' will be returned for stale dates

	if (!storeDate) return defaultValues;
	// Take the string date value in store
	let workingDate: Date | undefined;
	// Get a Date() value from it
	if (storeDate) workingDate = parseISO(storeDate);
	// Calclulate if the value has gone stale, return 'NOW' only
	if (workingDate && isBefore(workingDate, new Date())) {
		return nowValues;
		// If the date is still valid, populate the date/time values in the form
	} else {
		const cancellationDate: CancellationDateFormValues['cancellationDate'] = storeDate.split('T')[0];
		const cancellationTime: CancellationDateFormValues['cancellationTime'] = {
			hours: getHours(storeDate).toString().padStart(2, '0'),
			minutes: getMinutes(storeDate).toString().padStart(2, '0'),
		};
		return {
			cancellationDateDescription: storeDescription,
			cancellationDate,
			cancellationTime,
		};
	}
};

/**
 * Gets the initial values for the cancellation date form
 * Factors in default values and those transformed from the store.
 * @param storeCancellationDate - The stored cancellation date.
 * @returns The initial form values for the cancellation date.
 */
export const getInitialCancellationDateFormValues = (
	storeCancellationDate: CancellationDate | undefined,
): CancellationDateFormValues => {
	// Starting form values for the journey
	const defaultValues: CancellationDateFormValues = {
		cancellationDateDescription: '',
	};
	// If we have a storeCancellationDate, process it to populate the form - accounting for stale values
	if (!!storeCancellationDate && storeCancellationDate.description) {
		const workingStoreDate = structuredClone(storeCancellationDate); // prevent store mutation
		const date = workingStoreDate.date;
		const description = workingStoreDate.description;
		// If the current description is 'NOW' keep it this way and trim away any unnecessary fields if present
		if (description === 'NOW') return { cancellationDateDescription: description };
		// If the current description is 'LATER' process the datetime values
		if (description === 'LATER') return getLaterDateFormValues(date, description);
	}
	// If we don't have a 'NOW' or 'LATER' value, return the default values
	return defaultValues;
};
